// Área dos Pais — progresso de cada filho (Atividades, Idiomas e Momentos de atenção).
// Protegida por um "Portão dos Pais" (continha simples) para a criança não entrar sozinha.
// Todos os dados vêm do backend, que confere que os perfis são da conta logada.
import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CoreContext } from 'context/CoreContext';
import ContainerAuthenticated from 'containers/Authenticated';
import { montarRelatorio, getMemoria, apagarMemoria } from 'services/relatorio';
import { NOME_DOS_NIVEIS } from 'services/chatV4';
import {
  Wrap, Titulo, Subtitulo, AvisoBox, CartaoCrianca, NomeCrianca, Bloco, TituloBloco,
  LinhaSelos, Selo, Texto, Tranquilo, Atencao, BotaoApagar, Explicacao, PortaoWrap, PortaoTitulo, PortaoConta,
  PortaoInput, PortaoBotao, PortaoErro, Voltar,
} from './styled';

const EMOJI_SENT = {
  alegria: '😄', feliz: '😄', tristeza: '😢', triste: '😢', medo: '😨', raiva: '😠',
  bravo: '😠', ansiedade: '😟', calma: '😌', gratidao: '🙏', amor: '💛', surpresa: '😮',
};
const emojiSent = (s) => EMOJI_SENT[(s || '').toLowerCase()] || '💬';
const capitaliza = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const NOME_MASCOTE = { lulu: 'Lulu', nando: 'Nando' };

function PortaoDosPais({ aoLiberar }) {
  const [a] = useState(() => 2 + Math.floor(Math.random() * 8));
  const [b] = useState(() => 2 + Math.floor(Math.random() * 8));
  const [resp, setResp] = useState('');
  const [erro, setErro] = useState('');

  const conferir = (e) => {
    e.preventDefault();
    if (parseInt(resp, 10) === a + b) aoLiberar();
    else setErro('Ops, não é essa. Tente de novo. 🙂');
  };

  return (
    <PortaoWrap>
      <div style={{ fontSize: 40 }}>👨‍👩‍👧</div>
      <PortaoTitulo>Área dos Pais</PortaoTitulo>
      <Subtitulo>Só para confirmar que é um adulto:</Subtitulo>
      <form onSubmit={conferir}>
        <PortaoConta>Quanto é {a} + {b}?</PortaoConta>
        <PortaoInput
          type="number"
          value={resp}
          onChange={(e) => { setResp(e.target.value); setErro(''); }}
          autoFocus
        />
        <PortaoErro>{erro}</PortaoErro>
        <PortaoBotao type="submit">Entrar</PortaoBotao>
      </form>
    </PortaoWrap>
  );
}

function BlocoIdiomas({ idiomas }) {
  const linha = (chave) => {
    const d = idiomas[chave];
    if (!d) return null;
    const nivel = NOME_DOS_NIVEIS[d.nivel] || 'Explorador';
    const palavras = Array.isArray(d.palavras_recentes) ? d.palavras_recentes : [];
    return (
      <Texto key={chave}>
        <b>{NOME_MASCOTE[chave]}:</b> nível {nivel}
        {palavras.length > 0 ? ` — ${palavras.length} palavra(s) recente(s): ${palavras.slice(0, 8).join(', ')}` : ' — ainda começando'}
      </Texto>
    );
  };
  const temAlgo = idiomas.lulu || idiomas.nando;
  return (
    <Bloco>
      <TituloBloco>📚 Idiomas (no chat)</TituloBloco>
      {temAlgo ? [linha('lulu'), linha('nando')] : <Texto>Ainda não começou a conversar no modo Idiomas.</Texto>}
    </Bloco>
  );
}

function BlocoMomentos({ momentos }) {
  const { sentimentos, atencao, totalConversas } = momentos;
  return (
    <Bloco>
      <TituloBloco>💛 Momentos de atenção</TituloBloco>
      {totalConversas === 0 ? (
        <Texto>Ainda não há conversas registradas.</Texto>
      ) : (
        <>
          {sentimentos.length > 0 && (
            <Texto>
              Clima das conversas:{' '}
              {sentimentos.slice(0, 4).map(([s, n]) => `${emojiSent(s)} ${capitaliza(s)} (${n})`).join('   ')}
            </Texto>
          )}
          {atencao.length === 0 ? (
            <Tranquilo>✅ Nenhum tema delicado sinalizado nas conversas recentes.</Tranquilo>
          ) : (
            <Atencao>
              <b>🚩 {atencao.length} momento(s) que pedem seu carinho:</b>
              {atencao.slice(0, 6).map((n, i) => (
                <div key={i}>
                  • Com {NOME_MASCOTE[n.personagem] || 'mascote'} ({n.modo})
                  {n.sentimento ? ` — sentimento: ${capitaliza(n.sentimento)}` : ''}
                  {n.data ? ` — ${new Date(n.data).toLocaleDateString('pt-BR')}` : ''}
                </div>
              ))}
            </Atencao>
          )}
        </>
      )}
    </Bloco>
  );
}

// Bloco da memória: mostra QUANTAS lembranças o personagem guarda daquela criança
// e deixa a família apagar tudo quando quiser. O texto das conversas nunca aparece
// aqui — o backend devolve só o número.
function BlocoMemoria({ profileId, nome }) {
  const [dados, setDados] = useState(null);
  const [apagando, setApagando] = useState(false);
  const [recado, setRecado] = useState('');

  useEffect(() => {
    let vivo = true;
    getMemoria(profileId).then((r) => { if (vivo) setDados(r); });
    return () => { vivo = false; };
  }, [profileId]);

  const quantas = dados && typeof dados.lembrancas === 'number' ? dados.lembrancas : null;

  const aoApagar = async () => {
    const certeza = window.confirm(
      'Apagar tudo o que o personagem lembra das conversas de ' + (nome || 'sua filha(o)') + '?\n\n'
      + 'Ele vai continuar conversando normalmente — só começa do zero, sem lembrar do que já foi conversado.'
    );
    if (!certeza) return;
    setApagando(true);
    const r = await apagarMemoria(profileId);
    setApagando(false);
    if (r) { setDados({ lembrancas: 0 }); setRecado('Pronto! As lembranças foram apagadas. 💛'); }
    else setRecado('Não consegui apagar agora. Tente de novo daqui a pouco.');
  };

  return (
    <Bloco>
      <TituloBloco>🧠 O que o personagem lembra</TituloBloco>
      {quantas === null ? (
        <Texto>Carregando...</Texto>
      ) : (
        <>
          <LinhaSelos>
            <Selo cor="#eef7f0"><b>{quantas}</b>{quantas === 1 ? 'lembrança guardada' : 'lembranças guardadas'}</Selo>
          </LinhaSelos>
          <Explicacao>
            O personagem lembra das últimas conversas para ajudar no aprendizado — como as palavras
            que ela está treinando e os assuntos favoritos. Ele nunca usa isso para criar apego,
            nem guarda segredo. Você pode apagar tudo quando quiser.
          </Explicacao>
          <BotaoApagar onClick={aoApagar} disabled={apagando || quantas === 0}>
            {apagando ? 'Apagando...' : '🧹 Apagar as lembranças'}
          </BotaoApagar>
          {recado ? <Explicacao>{recado}</Explicacao> : null}
        </>
      )}
    </Bloco>
  );
}

function RelatorioCrianca({ profile }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    montarRelatorio(profile)
      .then((r) => { if (vivo) { setDados(r); setCarregando(false); } })
      .catch(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [profile]);

  const avatar = profile.avatar?.image || profile.avatar || '/images/default-avatar.png';

  return (
    <CartaoCrianca>
      <NomeCrianca><img src={avatar} alt="" />{profile.name || 'Criança'}</NomeCrianca>
      {carregando || !dados ? (
        <Texto>Carregando o progresso...</Texto>
      ) : (
        <>
          <Bloco>
            <TituloBloco>🎮 Atividades</TituloBloco>
            <LinhaSelos>
              <Selo cor="#e8f0fe"><b>{dados.atividades.completos}/{dados.atividades.totalMundos}</b>mundos completos</Selo>
              <Selo cor="#fff4d6"><b>⭐ {dados.atividades.totalEstrelas}</b>estrelas</Selo>
              <Selo cor="#ffe6e0"><b>🔥 {dados.atividades.dias}</b>dias seguidos</Selo>
              <Selo cor="#eee7fb"><b>🖼️ {dados.atividades.figurinhas}</b>figurinhas</Selo>
            </LinhaSelos>
          </Bloco>
          <BlocoIdiomas idiomas={dados.idiomas} />
          <BlocoMomentos momentos={dados.momentos} />
          <BlocoMemoria profileId={profile.id} nome={profile.name} />
        </>
      )}
    </CartaoCrianca>
  );
}

export default function DashboardAreaPais() {
  const { profiles } = useContext(CoreContext);
  const history = useHistory();
  const [liberado, setLiberado] = useState(false);
  const lista = Array.isArray(profiles) ? profiles : [];

  return (
    <ContainerAuthenticated primary page={'area-pais'}>
      {!liberado ? (
        <PortaoDosPais aoLiberar={() => setLiberado(true)} />
      ) : (
        <Wrap>
          <Voltar onClick={() => history.push('/dashboard/account')}>⬅ Voltar para Minha Conta</Voltar>
          <Titulo>👨‍👩‍👧 Área dos Pais</Titulo>
          <Subtitulo>Acompanhe o progresso e o bem-estar de cada filho.</Subtitulo>
          <AvisoBox>
            💡 Os "momentos de atenção" são gerados por inteligência artificial e servem como um
            <b> farol</b> para você olhar com carinho — não são um diagnóstico e não substituem a sua
            atenção nem a de um profissional. Na hora da conversa, os mascotes já orientam a criança a
            procurar um adulto de confiança em temas delicados.
          </AvisoBox>
          {lista.length === 0 ? (
            <Subtitulo>Nenhum perfil de criança cadastrado ainda.</Subtitulo>
          ) : (
            lista.map((p) => <RelatorioCrianca key={p.id} profile={p} />)
          )}
        </Wrap>
      )}
    </ContainerAuthenticated>
  );
}
